import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { useTranslation } from "react-i18next";
import "../shared/quiz-style-context.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useDragTextQuiz } from "../hooks/use-drag-text-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function DragTextStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
  blankPlaceholder
}) {
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit
  } = useDragTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && blankCount > 0) return [];
    const timestamp = problemKey;
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${timestamp}`,
      (index) => {
        var _a, _b;
        const value = answers[index] || "";
        const valueLabel = resolveLabel(value);
        const isActive = currentIndex === index && !isSolved && !isViewSolved;
        const hasValue = value !== "";
        const isCorrect = isSolved ? (_a = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a[index] : null;
        const correctAnswer = ((_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index]) ?? "";
        const correctAnswerLabel = resolveLabel(correctAnswer);
        const showCorrectAnswer = shouldShowCorrectAnswers && correctAnswerLabel !== "";
        const isCompleted = isSolved || isViewSolved;
        return /* @__PURE__ */ jsx(
          "span",
          {
            onClick: () => handleBlankClick(index),
            className: cn(
              "min-w-[48px] px-4 relative my-1 text-[24px] font-medium rounded-2xl mx-2 inline-flex items-center h-12 border-2 border-lc-gray-400 [box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.1)] transition-all duration-200",
              hasValue ? "justify-between" : "justify-center",
              // Hover (only when not completed)
              !isCompleted && "hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.3)]",
              // Active (current index, no value)
              isActive && !hasValue && "min-w-[96px] bg-lc-lightblue-200 border-lc-lightblue-900 [box-shadow:unset]",
              // Active (current index, has value)
              isActive && hasValue && "min-w-[96px] bg-lc-lightblue-200 border-lc-lightblue-900 [box-shadow:unset]",
              // Has value, not active, not solved
              !isSolved && !isViewSolved && hasValue && !isActive && "bg-lc-lightblue-200 not-[&:hover]:border-none [box-shadow:unset]",
              // Correct
              !showCorrectAnswer && isSolved && isCorrect === true && "bg-lc-mint-200 text-lc-mint-900 border-none pointer-events-none [box-shadow:unset]",
              // Wrong (not showing correct answers)
              !showCorrectAnswer && isSolved && isCorrect === false && "bg-lc-red-200 text-lc-red-900 border-none pointer-events-none [box-shadow:unset]",
              // Showing correct answers, including an unanswered blank
              showCorrectAnswer && "bg-lc-mint-200 text-lc-mint-900 border-none pointer-events-none [box-shadow:unset]",
              // Completed (view solved)
              isViewSolved && "border-none pointer-events-none [box-shadow:unset]"
            ),
            children: showCorrectAnswer ? /* @__PURE__ */ jsx(KatexText, { text: correctAnswerLabel }) : hasValue ? /* @__PURE__ */ jsxs("span", { className: "flex gap-1 items-center", children: [
              /* @__PURE__ */ jsx(KatexText, { text: valueLabel }),
              !isSolved && !isViewSolved && /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    handleClearBlank(index);
                  },
                  className: "w-6 h-6 ml-2 bg-lc-gray-400 rounded-full flex items-center justify-center shrink-0",
                  children: /* @__PURE__ */ jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx("path", { d: "M1 1L11 11M11 1L1 11", stroke: "white", strokeWidth: "2", strokeLinecap: "round" }) })
                }
              )
            ] }) : /* @__PURE__ */ jsx("span", { className: "text-lc-gray-400", children: blankPlaceholder ?? t("types.dragText.blank") })
          },
          `blank-${quizId}-${timestamp}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [answers, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, blankCount, handleBlankClick, handleClearBlank, problemKey, quiz.additionalImages, resolveLabel, blankPlaceholder, t]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.dragText.label"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        dimTitleText: dimChoiceText,
        dimMedia: dimChoiceText
      }
    ),
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        "data-quiz-problem-text": true,
        className: cn(
          "text-2xl font-medium text-gray-700",
          dimChoiceText && "[&_.formatProblem]:opacity-[0.05] [&_.formatProblem_other]:opacity-[0.05]"
        ),
        children: renderedProblem
      }
    ),
    availableOptions.length > 0 && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: 0.2 },
        className: "w-full bg-lc-gray-100 p-6 rounded-2xl mt-10",
        children: /* @__PURE__ */ jsx("div", { className: "flex flex-row gap-3 flex-wrap", children: availableOptions.map((option, idx) => {
          const isCurrentlySelected = answers[currentIndex] === option.key;
          return /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleSelectOption(option.key),
              disabled: isSolved || isViewSolved,
              className: cn(
                "text-[24px] font-medium min-w-[48px] h-12 flex text-gray-700 items-center justify-center bg-white rounded-2xl border-2 border-lc-gray-400 cursor-pointer [box-shadow:0px_4px_0px_#E0E0E0] px-4 py-3 transition-all duration-150",
                !isSolved && !isViewSolved && "hover:border-lc-lightblue-900 hover:text-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.30)]",
                (isSolved || isViewSolved) && "cursor-default",
                isCurrentlySelected && "border-lc-lightblue-900 bg-lc-lightblue-200 [box-shadow:unset]"
              ),
              children: /* @__PURE__ */ jsx("span", { "data-quiz-choice-text": true, className: cn(dimChoiceText && "opacity-[0.05]"), children: /* @__PURE__ */ jsx(KatexText, { text: option.label }) })
            },
            option.key || `option-${idx}`
          );
        }) })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
export {
  DragTextStudent
};
//# sourceMappingURL=drag-text-student.js.map
